import axios, { CancelTokenSource } from 'axios'
import { debounce } from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { LoaderFunctionArgs, Outlet, useLoaderData, useParams } from 'react-router'

import { useEnableNotificationsPrompt } from '../../hooks/useEnableNotificationsPrompt'
import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePSNavigate } from '../../hooks/usePSNavigate'
import { usePageTitle } from '../../hooks/usePageTitle'
import { BaseRecord } from '../../models/BaseRecord'
import { Message } from '../../models/Message'
import { RecordFactory } from '../../models/RecordFactory'
import { SQLiteFactory } from '../../models/SQLiteFactory'
import { Team } from '../../models/Team'
import { User } from '../../models/User'
import { generateCancelToken } from '../../modules/apiclient'
import { useNotificationListener } from '../../providers/NotificationsManagerProvider'
import { PSApplication } from '../app/PSApplication'
import { TeamView } from './TeamView'
import { InboxView } from './InboxView'
import { SelectRecipientsView } from './SelectRecipientsView'
import { getUpdatedInbox } from './messagesDataTransforms'

type MessagesViewData = {
    inbox: Message[]
    messages: Message[]
    thread: BaseRecord | null
    unreadCount: number
    inboxTimestamp?: moment.Moment
    conversationTimestamp?: moment.Moment
    error?: string
}

type SearchState<T> = {
    searchText: string
    isSearching: boolean
    didSearch: boolean
    searchResults: T[]
}

export type ConversationState = {
    isLoading: boolean
    currentThread: BaseRecord | null
    messages: Message[]
    inputBarHeight: number
    showNewMessage: boolean
    showCreateGroup: boolean
    newConversation: SearchState<User> & {
        selectedUsers: User[]
    }
    pagination: {
        justScrolled: boolean
        hasMore: boolean
        timestamp?: moment.Moment
    }
    error?: string
}

export type InboxState = SearchState<Message> & {
    isSending: boolean
    inbox: Message[]
    unreadCount: number
    pagination: {
        isScrolling: boolean
        hasMore: boolean
        timestamp?: moment.Moment
    }
}

export const loader = async ({ params }: LoaderFunctionArgs): Promise<MessagesViewData> => {
    const data: MessagesViewData = {
        inbox: [],
        messages: [],
        thread: null,
        unreadCount: 0,
    }

    const { threadID } = params

    const result = await RecordFactory.getInstance().fetchInbox(undefined, undefined)
    if (result.ok) {
        data.inbox = result.value
        data.inboxTimestamp = result.value.length
            ? result.value[result.value.length - 1].threadLastModifiedDate
            : undefined
    }
    else {
        throw result.error
    }

    data.unreadCount = (await SQLiteFactory.getInstance().badgeCount()) || 0

    if (threadID) {
        const thread =
            (await RecordFactory.getInstance().loadUser(threadID)) ||
            (await RecordFactory.getInstance().loadTeam(threadID))

        if (thread) {
            const result = await RecordFactory.getInstance().fetchThread(thread)

            if (!result.ok) {
                data.error = (result.error as any).toString()
                throw result.error
            }

            data.messages = result.value
            data.thread = thread
            data.conversationTimestamp = result.value.length
                ? result.value[result.value.length - 1].createdDate
                : undefined
        }
    }

    // If we are coming from another page and opening messages,
    // we need to mark the current selected thread as _read_,
    // but only if the the last message in the thread has not been viewed.
    if (data.messages.length) {
        const lastMessage = data.messages[0]
        const isInbound = PSApplication.me().id !== lastMessage.sender?.id
        const isUnread = !lastMessage.viewedDate

        if (isInbound && isUnread) {
            const result = await RecordFactory.getInstance().markRead(lastMessage)
            if (result.ok) {
                data.unreadCount = result.value.badge || 0
                data.inbox = result.value.messages?.length
                    ? getUpdatedInbox(
                          data.inbox,
                          result.value.messages[0],
                          data.thread instanceof Team
                      )
                    : []
            }
            else {
                throw result.error
            }
        }
    }

    return data
}

const MessagesPage = () => {
    usePageTitle('Messages')

    const data = useLoaderData() as Awaited<ReturnType<typeof loader>>
    const { threadID } = useParams()

    const { logAndDisplayError } = useErrorHandler()

    const navigate = usePSNavigate()
    const [inboxHeight, setInboxHeight] = useState(window.innerHeight - 32)
    const [conversationState, setConversationState] = useState<ConversationState>({
        isLoading: false,
        currentThread: data.thread,
        messages: data.messages,
        error: data.error,
        inputBarHeight: 20,
        showNewMessage: false,
        showCreateGroup: false,
        newConversation: {
            searchText: '',
            isSearching: false,
            didSearch: false,
            searchResults: [],
            selectedUsers: [],
        },
        pagination: {
            justScrolled: false,
            hasMore: true,
            timestamp: data.conversationTimestamp,
        },
    })

    useEnableNotificationsPrompt()

    const [inboxState, setInboxState] = useState<InboxState>({
        isSending: false,
        inbox: data.inbox,
        unreadCount: data.unreadCount,
        searchText: '',
        isSearching: false,
        didSearch: false,
        searchResults: [],
        pagination: {
            isScrolling: false,
            hasMore: true,
            timestamp: data.inboxTimestamp,
        },
    })

    const { isLoading, currentThread, messages, showNewMessage, showCreateGroup } =
        conversationState

    const isFetchingInboxRef = useRef(false)
    const cancelInboxFetchTokenRef = useRef<CancelTokenSource | null>(null)
    const cancelInboxSearchTokenRef = useRef<CancelTokenSource | null>(null)
    const cancelUserSearchTokenRef = useRef<CancelTokenSource | null>(null)

    const cancelPreviousInboxFetchRequest = () => {
        if (cancelInboxFetchTokenRef.current) {
            cancelInboxFetchTokenRef.current.cancel(
                'Previous inbox fetch request cancelled due to new request.'
            )
        }
        cancelInboxFetchTokenRef.current = generateCancelToken()
    }

    const cancelPreviousInboxSearchRequest = () => {
        if (cancelInboxSearchTokenRef.current) {
            cancelInboxSearchTokenRef.current.cancel(
                'Previous inbox search request cancelled due to new request.'
            )
        }
        cancelInboxSearchTokenRef.current = generateCancelToken()
    }

    const clearInboxSearch = () => {
        if (cancelInboxSearchTokenRef.current) {
            searchInboxDebounced.cancel()
            cancelPreviousInboxSearchRequest()
        }

        setInboxState((prev) => ({
            ...prev,
            searchText: '',
            isSearching: false,
            didSearch: false,
            searchResults: [],
        }))
    }

    const createGroup = (group: User[]) => {
        setConversationState((prev) => ({
            ...prev,
            showCreateGroup: true,
            newConversation: {
                ...prev.newConversation,
                selectedUsers: group,
            },
        }))
    }

    const cancelPreviousUserSearchRequest = () => {
        if (cancelUserSearchTokenRef.current) {
            cancelUserSearchTokenRef.current.cancel(
                'Previous user search request cancelled due to new request.'
            )
        }
        cancelUserSearchTokenRef.current = generateCancelToken()
    }

    const searchUsersDebounced = debounce(
        async (query: string) => {
            cancelPreviousUserSearchRequest()
            setConversationState((prev) => ({
                ...prev,
                newConversation: {
                    ...prev.newConversation,
                    isSearching: true,
                },
            }))

            const result = await RecordFactory.getInstance().searchUser(
                query,
                undefined,
                cancelUserSearchTokenRef.current?.token
            )
            if (!result.ok) {
                if (axios.isCancel(result.error)) {
                    console.log('Cancelled user search:', (result.error as any).message)
                }
                else {
                    logAndDisplayError(result.error)
                    setConversationState((prev) => ({
                        ...prev,
                        newConversation: {
                            ...prev.newConversation,
                            isSearching: false,
                        },
                    }))
                }
                return
            }
            setConversationState((prev) => ({
                ...prev,
                newConversation: {
                    ...prev.newConversation,
                    isSearching: false,
                    didSearch: true,
                    searchResults: result.value.filter(
                        (searchResult) => searchResult instanceof User
                    ),
                },
            }))
        },
        250,
        { trailing: true }
    )

    const searchUsers = (query: string) => {
        setConversationState((prev) => ({
            ...prev,
            newConversation: {
                ...prev.newConversation,
                searchText: query,
                isSearching: true,
            },
        }))
        searchUsersDebounced(query)
    }

    const resetConversation = () => {
        setConversationState((prev) => ({
            ...prev,
            showNewMessage: false,
            showCreateGroup: false,
            newConversation: {
                ...prev.newConversation,
                selectedUsers: [],
                searchText: '',
            },
        }))
    }

    const onMessagesNotification = useCallback(
        (data: Message | Message[]) => {
            if (isLoading || !data) return

            if (typeof data === 'object' && data instanceof Message && !Array.isArray(data)) {
                data = [data]
            }

            if (Array.isArray(data)) {
                const messageIds = messages.map((m) => m.id)
                const newMessages = data
                    .filter((m) => m.channel && ['sms', 'push'].includes(m.channel))
                    .filter((m) => !messageIds.includes(m.id))

                newMessages.forEach((message) => {
                    const isGroup = currentThread instanceof Team
                    const isGroupMessage = isGroup && message.team.id === currentThread.id

                    if (
                        message.recipient?.id === currentThread?.id ||
                        message.sender?.id === currentThread?.id ||
                        isGroupMessage
                    ) {
                        setConversationState((prev) => ({
                            ...prev,
                            messages: [message, ...messages],
                            pagination: {
                                ...prev.pagination,
                                justScrolled: false,
                            },
                        }))

                        setInboxState((prev) => ({
                            ...prev,
                            inbox: getUpdatedInbox(prev.inbox, message, isGroup),
                        }))
                    }
                })
            }
        },
        [isLoading, messages, currentThread]
    )

    const getUnreadCount = async () => {
        const unreadCount = (await SQLiteFactory.getInstance().badgeCount()) || 0
        setInboxState((prev) => ({ ...prev, unreadCount }))
    }

    useNotificationListener('MessagesDataSourceChanged', onMessagesNotification)
    useNotificationListener('MessagesTabDataSourceChanged', getUnreadCount)

    useEffect(() => {
        const handleResize = () => {
            setInboxHeight(window.innerHeight - 32)
        }
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    const fetchInbox = async (createdBeforeDate?: moment.Moment) => {
        isFetchingInboxRef.current = true

        cancelPreviousInboxFetchRequest()
        setInboxState((prev) => ({
            ...prev,
            isSending: true,
            pagination: {
                ...prev.pagination,
                isScrolling: false,
            },
        }))

        const result = await RecordFactory.getInstance().fetchInbox(
            undefined,
            createdBeforeDate,
            cancelInboxFetchTokenRef.current?.token
        )
        isFetchingInboxRef.current = false

        if (!result.ok) {
            logAndDisplayError(result.error)
            setInboxState((prev) => ({ ...prev, isSending: false }))
            return
        }

        const hasMore = result.value.length > 0
        const lastMessageTimestamp = hasMore
            ? result.value[result.value.length - 1].threadLastModifiedDate
            : undefined

        setInboxState((prev) => ({
            ...prev,
            isSending: false,
            inbox: [...prev.inbox, ...result.value],
            pagination: {
                ...prev.pagination,
                isScrolling: false,
                hasMore,
                timestamp: lastMessageTimestamp,
            },
        }))
    }

    const loadMoreInbox = () => {
        const { hasMore, timestamp } = inboxState.pagination

        if (!isFetchingInboxRef.current && hasMore) {
            fetchInbox(timestamp)
        }
    }

    const createGroupMessage = (group: Team) => {
        if (!group.id) return
        navigate(`/messages/${group.id}`)
    }

    const searchInboxDebounced = debounce(
        async (query: string) => {
            cancelPreviousInboxSearchRequest()
            setInboxState((prev) => ({ ...prev, isSearching: true }))

            const result = await RecordFactory.getInstance().searchMessenger(
                query,
                undefined,
                cancelInboxSearchTokenRef.current?.token
            )
            if (!result.ok) {
                if (axios.isCancel(result.error)) {
                    console.log('Cancelled inbox search:', (result.error as any).message)
                }
                else {
                    logAndDisplayError(result.error)
                    setInboxState((prev) => ({ ...prev, isSearching: false }))
                }
                return
            }
            setInboxState((prev) => ({
                ...prev,
                isSearching: false,
                didSearch: true,
                searchResults: result.value,
            }))
        },
        250,
        { trailing: true }
    )

    const searchInbox = (query: string) => {
        setInboxState((prev) => ({
            ...prev,
            searchText: query,
            isSearching: true,
        }))
        searchInboxDebounced(query)
    }

    return (
        <div className="grid h-full max-w-full grid-cols-[440px_1fr]">
            <div className="flex h-full flex-1">
                <InboxView
                    inboxState={inboxState}
                    currentThreadID={threadID}
                    inboxHeight={inboxHeight}
                    onSelectThread={(thread: BaseRecord) => navigate(`/messages/${thread.id}`)}
                    onNewMessage={() =>
                        setConversationState((prev) => ({ ...prev, showNewMessage: true }))
                    }
                    onSearch={searchInbox}
                    onClearSearch={clearInboxSearch}
                    onLoadMore={loadMoreInbox}
                />
            </div>
            <div className="flex h-screen min-w-0 flex-2 flex-col overflow-hidden">
                {showCreateGroup ? (
                    <TeamView
                        conversationState={conversationState}
                        onCreateGroupMessage={createGroupMessage}
                        onBack={() =>
                            setConversationState((prev) => ({ ...prev, showCreateGroup: false }))
                        }
                    />
                ) : showNewMessage ? (
                    <SelectRecipientsView
                        conversationState={conversationState}
                        inboxHeight={inboxHeight}
                        onCreateGroup={createGroup}
                        onSelectUser={(userId: string) => {
                            setConversationState((prev) => ({
                                ...prev,
                                showNewMessage: false,
                            }))
                            navigate(`/messages/${userId}`)
                        }}
                        onSearch={searchUsers}
                        onBack={resetConversation}
                    />
                ) : threadID !== undefined ? (
                    <Outlet />
                ) : inboxState.inbox.length === 0 ? (
                    <div className="emptyState">
                        <div>Send a message to get started 💬</div>
                    </div>
                ) : (
                    <div className="emptyState grow">
                        <div>Select a message to get started</div>
                        <div className="unread">
                            You have {inboxState.unreadCount} unread message
                            {inboxState.unreadCount !== 1 ? 's' : ''} 💬
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

// Unfortunately, like the ThreadView component, MessagesPage isn't properly
// implemented and doesn't respond to changes in inputs. In the interests of time,
// I'm using this hack to just completely remount the component when anything changes.
export const MessagesPageWrapper = () => {
    const data = useLoaderData() as Awaited<ReturnType<typeof loader>>

    return <MessagesPage key={JSON.stringify(data)} />
}
